import { useState, useEffect } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { getEligibleApproaches } from '../api/marketplaceApi';
import type { TemplateApproach } from '../types/templateApproach';
import type { LocalizedString } from 'types/context';
import { resolveLocalizedString } from '../utils/resolveLocalizedString';

export interface UseEligibleApproachesResult {
  approaches: TemplateApproach[];
  loading: boolean;
  error: string | null;
}

export function useEligibleApproaches(sphereId: string | null): UseEligibleApproachesResult {
  const apiClients = useApiClientsOrNull();

  const [approaches, setApproaches] = useState<TemplateApproach[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sphereId || !apiClients?.graphClient) {
      setApproaches([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchEligible() {
      setLoading(true);
      setError(null);

      try {
        const response = await getEligibleApproaches(apiClients!.graphClient, sphereId!);
        const mapped: TemplateApproach[] = response.approaches.map((a) => ({
          id: a.id,
          system_name: a.system_name,
          name: resolveLocalizedString(a.name as LocalizedString | string),
          description: resolveLocalizedString(a.description as LocalizedString | string | null),
          use_case_tags: [],
          applicability: a.applicability,
          templateFor: a.templateFor,
          childCount: a.childCount,
          factorCount: a.factorCount,
          metaspaceCount: a.metaspaceCount,
          official: a.official ?? false,
          isNew: a.isNew ?? false,
          suggestedBy: a.suggestedBy ?? [],
          channel: a.channel
            ? {
                id: a.channel.id,
                system_name: a.channel.system_name ?? '',
                name: a.channel.name,
                subscribed: true,
              }
            : undefined,
        }));

        if (!cancelled) {
          setApproaches(mapped);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load eligible approaches');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchEligible();

    return () => {
      cancelled = true;
    };
  }, [apiClients, sphereId]);

  return { approaches, loading, error };
}
